import { useMemo } from "react";
import { useGetProductCategoriesQuery } from "graphql/queries/get-product-categories.generated";

// Product category type
export interface ProductCategory {
	name: string; // e.g., "ACCESS_CONTROL" - used for DB
	label: string; // e.g., "Access Control" - used for UI display
}

/**
 * Hook to get product categories with utility functions
 * Returns categories, a function to get label from name, and loading state
 */
export const useProductCategories = () => {
	const { data, loading, error } = useGetProductCategoriesQuery({
		errorPolicy: "none",
		fetchPolicy: "cache-first", // Use cache-first for better performance
		notifyOnNetworkStatusChange: true
	});

	// Create a map for fast lookup: name -> category object
	const categoryMap = useMemo(() => {
		const map = new Map<string, ProductCategory>();

		if (data?.product_categories) {
			data.product_categories.forEach(category => {
				map.set(category.name, {
					name: category.name,
					label: category.label
				});
			});
		}

		return map;
	}, [data]);

	// Function to get label from name (e.g., "ACCESS_CONTROL" -> "Access Control")
	const getLabelFromName = (name: string): string => {
		const category = categoryMap.get(name);
		return category?.label || name;
	};

	// Function to get category object from name
	const getCategoryFromName = (name: string): ProductCategory | null => {
		return categoryMap.get(name) || null;
	};

	// Function to get name from label (e.g., "Access Control" -> "ACCESS_CONTROL")
	const getNameFromLabel = (label: string): string => {
		if (data?.product_categories) {
			const category = data.product_categories.find(cat => cat.label === label);
			return category?.name || label;
		}
		return label;
	};

	// Get categories as options for Select component
	const categoryOptions = useMemo(() => {
		if (!data?.product_categories) return [];

		return data.product_categories.map(category => ({
			value: category.name, // This is what gets saved to DB (e.g., "ACCESS_CONTROL")
			label: category.label // This is what's displayed (e.g., "Access Control")
		}));
	}, [data]);

	// Get all categories as ProductCategory objects
	const categories: ProductCategory[] = useMemo(() => {
		if (!data?.product_categories) return [];

		return data.product_categories.map(cat => ({
			name: cat.name,
			label: cat.label
		}));
	}, [data]);

	return {
		categories,
		categoryOptions,
		categoryMap,
		getLabelFromName,
		getCategoryFromName,
		getNameFromLabel,
		loading,
		error
	};
};
